import { isRouteHandle } from "@sg-protect/federation-runtime";
import { MixpanelPropertiesProvider } from "@workleap/telemetry/react";
import { type PropsWithChildren, useMemo } from "react";
import { type UIMatch, useMatches, useSearchParams } from "react-router";
import { MixpanelProperty } from "./mixpanelProperty.ts";

export function getRouteMixpanelProperties(
    matches: readonly Pick<UIMatch, "handle" | "params">[],
    searchParams: URLSearchParams
) {
    const routes = matches
        .flatMap(({ handle, params }) => (isRouteHandle(handle) ? [{ handle, params }] : []))
        .toReversed();
    const locationIndex = routes.findIndex(route => route.handle.mixpanelLocation !== undefined);
    const locationRoute = routes[locationIndex];

    if (!locationRoute) {
        return {};
    }

    const sublocationRoute = routes
        .slice(0, locationIndex + 1)
        .find(route => route.handle.mixpanelSublocation !== undefined);
    const sublocationSetting = sublocationRoute?.handle.mixpanelSublocation;
    const sublocation =
        typeof sublocationSetting === "function"
            ? sublocationSetting({ params: sublocationRoute?.params ?? {}, searchParams })
            : sublocationSetting;

    return {
        [MixpanelProperty.Location]: locationRoute.handle.mixpanelLocation,
        ...(sublocation ? { [MixpanelProperty.Sublocation]: sublocation } : {})
    };
}

export function RouteMixpanelPropertiesProvider({ children }: PropsWithChildren) {
    const matches = useMatches();
    const [searchParams] = useSearchParams();
    const properties = useMemo(() => getRouteMixpanelProperties(matches, searchParams), [matches, searchParams]);

    return <MixpanelPropertiesProvider value={properties}>{children}</MixpanelPropertiesProvider>;
}
